import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { landListingsAPI } from '../../services/landListingsAPI';
import { getPhoneAuthToken } from '../../services/auth';
import LandCard from '../common/LandCard';
import { useLanguage } from '../../contexts/LanguageContext';

const TYPE_TRANSLATION_MAP = {
  residential: 'search.landTypes.residential',
  layout: 'search.landTypes.layout',
  farm: 'search.landTypes.farm',
  agricultural: 'search.landTypes.farm',
  commercial: 'search.landTypes.commercial',
  industrial: 'search.landTypes.industrial',
};

const TypeSection = ({ typeName, lands, likedIds, onToggleLike, onCardClick, navigate }) => {
  const { t } = useLanguage();
  const limit = 10;
  const total = lands.length;
  const shown = lands.slice(0, limit);

  const translationKey = TYPE_TRANSLATION_MAP[typeName?.toLowerCase()];
  const displayName = translationKey ? t(translationKey) : typeName;

  return (
    <div className="mb-10">
      {/* Section Header */}
      <div className="flex items-center justify-between mb-4 px-1">
        <div className="flex items-center gap-3">
          <h3 className="text-lg md:text-xl font-semibold text-gray-900 tracking-tight font-montserrat">
            {displayName}
          </h3>
        </div>
        {total > limit && (
          <button
            onClick={() => navigate(`/search?type=${encodeURIComponent(typeName)}`)}
            className="text-sm font-medium text-gray-500 hover:text-gray-900 transition-colors flex items-center gap-1 group"
          >
            View all
            <svg className="w-4 h-4 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        )}
      </div>

      {/* Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {shown.map((listing) => (
          <LandCard
            key={listing.id}
            listing={listing}
            liked={likedIds.has(listing.id)}
            onToggleLike={() => onToggleLike(listing)}
            onClick={() => onCardClick(listing)}
            showPrice={false}
            showStatus={false}
          />
        ))}
      </div>
    </div>
  );
};

const LandTypes = () => {
  const navigate = useNavigate();
  const { featuredListings, loading } = useSelector((state) => state.landListings);
  const isLoggedIn = useSelector((state) => state.auth.isLoggedIn);
  const [likedIds, setLikedIds] = useState(new Set());

  useEffect(() => {
    const initLikes = async () => {
      try {
        const token = getPhoneAuthToken();
        if (!token) { setLikedIds(new Set()); return; }
        const res = await landListingsAPI.getUserLikes();
        const ids = new Set((res?.data || []).map((l) => l.property_id || l.properties?.id).filter(Boolean));
        setLikedIds(ids);
      } catch { }
    };
    initLikes();
  }, [isLoggedIn]);

  const toggleLike = async (listing) => {
    const id = listing?.id;
    if (!id) return;
    const token = getPhoneAuthToken();
    if (!token) return;
    try {
      if (likedIds.has(id)) {
        await landListingsAPI.unlikeProperty(id);
        const next = new Set(likedIds); next.delete(id); setLikedIds(next);
      } else {
        await landListingsAPI.likeProperty(id);
        const next = new Set(likedIds); next.add(id); setLikedIds(next);
      }
    } catch { }
  };

  const handleCardClick = (listing) => {
    landListingsAPI.trackPropertyView({
      property_id: listing.id,
      session_id: `sess_${Math.random().toString(36).slice(2, 10)}`,
      source: 'website',
    }).catch(() => {});
    navigate(`/land-details/${listing.id}`);
  };

  // Group public listings by property_type (fallback to type)
  const groupedByType = useMemo(() => {
    const public_ = featuredListings.filter((l) => l.status !== 'Private');
    const map = new Map();
    public_.forEach((l) => {
      const key = l.property_type || l.type || 'Other';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(l);
    });
    // Sort groups by count descending so the largest type appears first
    return Array.from(map.entries()).sort((a, b) => b[1].length - a[1].length);
  }, [featuredListings]);

  if (loading && groupedByType.length === 0) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-gray-100 rounded-2xl h-80 animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  if (groupedByType.length === 0) return null;

  return (
    <div className="max-w-7xl mx-auto px-4 py-10">
      {groupedByType.map(([typeName, lands]) => (
        <TypeSection
          key={typeName}
          typeName={typeName}
          lands={lands}
          likedIds={likedIds}
          onToggleLike={toggleLike}
          onCardClick={handleCardClick}
          navigate={navigate}
        />
      ))}
    </div>
  );
};

export default LandTypes;
